/**
 * Global Search Dialog Component
 * Command palette for searching across entities
 */

import { useEffect, useCallback } from 'react'
import { useTranslation, useSelector, useDispatch, useNavigate } from '@ufc/core'
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  cn,
  Spinner,
  Package,
  FileText,
  Truck,
  MapPin,
  User,
  Calendar,
  DoorOpen,
  Layers,
  Car,
  Container,
  Building2,
  Receipt,
  Boxes,
} from '@ufc/uikit'
import { globalSearchActions } from '../../store/global-search.slice'
import {
  selectSearchQuery,
  selectGroupedResults,
  selectSearchIsLoading,
  selectSearchIsOpen,
  selectHasResults,
  selectSearchError,
} from '../../store/global-search.selectors'
import type { SearchResult, SearchResultType } from '../../domain/types'

const TYPE_ICONS: Record<SearchResultType, typeof Package> = {
  order: Package,
  'sales-order': FileText,
  shipment: Truck,
  location: MapPin,
  visitor: User,
  visit: Calendar,
  gate: DoorOpen,
  zone: Layers,
  driver: Car,
  asset: Container,
  tenant: Building2,
  invoice: Receipt,
  product: Boxes,
}

const TYPE_ROUTES: Record<SearchResultType, string> = {
  order: '/orders',
  'sales-order': '/sales-orders',
  shipment: '/shipments',
  location: '/addressbook',
  visitor: '/visitors',
  visit: '/visits',
  gate: '/gates',
  zone: '/zones',
  driver: '/drivers',
  asset: '/assets',
  tenant: '/tenants',
  invoice: '/invoices',
  product: '/products',
}

interface SearchResultItemProps {
  result: SearchResult
  onSelect: (result: SearchResult) => void
}

function SearchResultItem({ result, onSelect }: SearchResultItemProps) {
  const Icon = TYPE_ICONS[result.type]

  return (
    <CommandItem
      value={`${result.type}-${result.id}-${result.title}`}
      onSelect={() => onSelect(result)}
      className="flex items-center gap-3 py-3"
    >
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="font-medium truncate">{result.title}</span>
        <span className="text-xs text-muted-foreground truncate">
          {result.subtitle}
        </span>
      </div>
      {result.status && (
        <span
          className={cn(
            'ms-auto text-xs px-2 py-0.5 rounded-full shrink-0',
            result.status === 'pending' && 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
            result.status === 'processing' && 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
            result.status === 'delivered' && 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
            result.status === 'completed' && 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
            result.status === 'in_transit' && 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
            result.status === 'cancelled' && 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
            result.status === 'canceled' && 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
          )}
        >
          {result.status}
        </span>
      )}
    </CommandItem>
  )
}

export function GlobalSearchDialog() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const isOpen = useSelector(selectSearchIsOpen)
  const query = useSelector(selectSearchQuery)
  const groupedResults = useSelector(selectGroupedResults)
  const isLoading = useSelector(selectSearchIsLoading)
  const hasResults = useSelector(selectHasResults)
  const error = useSelector(selectSearchError)

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        dispatch(globalSearchActions.openSearch())
      } else {
        dispatch(globalSearchActions.closeSearch())
      }
    },
    [dispatch]
  )

  const handleSearch = useCallback(
    (value: string) => {
      dispatch(globalSearchActions.searchRequest(value))
    },
    [dispatch]
  )

  const handleSelect = useCallback(
    (result: SearchResult) => {
      dispatch(globalSearchActions.closeSearch())
      const basePath = TYPE_ROUTES[result.type]
      navigate({ to: basePath, search: { id: String(result.id) } })
    },
    [dispatch, navigate]
  )

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        handleOpenChange(!isOpen)
      }
    }

    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [isOpen, handleOpenChange])

  const getGroupLabel = (type: SearchResultType): string => {
    const labels: Record<SearchResultType, string> = {
      order: t('globalSearch.results.orders'),
      'sales-order': t('globalSearch.results.salesOrders'),
      shipment: t('globalSearch.results.shipments'),
      location: t('globalSearch.results.locations'),
      visitor: t('globalSearch.results.visitors'),
      visit: t('globalSearch.results.visits'),
      gate: t('globalSearch.results.gates'),
      zone: t('globalSearch.results.zones'),
      driver: t('globalSearch.results.drivers'),
      asset: t('globalSearch.results.assets'),
      tenant: t('globalSearch.results.tenants'),
      invoice: t('globalSearch.results.invoices'),
      product: t('globalSearch.results.products'),
    }
    return labels[type]
  }

  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={handleOpenChange}
      title={t('globalSearch.title')}
      description={t('globalSearch.placeholder')}
      className="max-w-2xl"
      showCloseButton={false}
    >
      <CommandInput
        placeholder={t('globalSearch.placeholder')}
        value={query}
        onValueChange={handleSearch}
      />
      <CommandList className="max-h-[400px]">
        {isLoading && (
          <div className="flex items-center justify-center py-6">
            <Spinner className="h-4 w-4 me-2" />
            <span className="text-sm text-muted-foreground">
              {t('globalSearch.loading')}
            </span>
          </div>
        )}

        {error && (
          <div className="py-6 text-center text-sm text-destructive">
            {t('globalSearch.error')}
          </div>
        )}

        {!isLoading && !error && query.length > 0 && query.length < 2 && (
          <CommandEmpty>{t('globalSearch.minChars')}</CommandEmpty>
        )}

        {!isLoading && !error && query.length >= 2 && !hasResults && (
          <CommandEmpty>
            <div className="flex flex-col items-center gap-1">
              <span>{t('globalSearch.noResults')}</span>
              <span className="text-xs text-muted-foreground">
                {t('globalSearch.noResultsDescription')}
              </span>
            </div>
          </CommandEmpty>
        )}

        {!isLoading && !error && hasResults && (
          <>
            {Object.entries(groupedResults).map(([type, results], index) => (
              <div key={type}>
                {index > 0 && <CommandSeparator />}
                <CommandGroup heading={getGroupLabel(type as SearchResultType)}>
                  {results.map((result) => (
                    <SearchResultItem
                      key={`${result.type}-${result.id}`}
                      result={result}
                      onSelect={handleSelect}
                    />
                  ))}
                </CommandGroup>
              </div>
            ))}
          </>
        )}
      </CommandList>
    </CommandDialog>
  )
}
